import * as React from 'react';
import { useState, useRef, useCallback } from 'react';
import { FaTimes, FaTasks } from 'react-icons/fa';
import CrmTaskService, { CrmTask } from '../../sales/services/CrmTaskService';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import dateUtil from '../../../utils/dateUtil';
import ShiftService from '../services/ShiftService';
import { HttpError } from '../../../core/services/HttpService';
import shiftErrorHandler from '../services/ShiftErrorHandler';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import { toast } from 'react-toastify';

interface AddShiftModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  workAreaOptions: any[];
  initialData: {
    start_time_date: string;
    start_time_time: string;
    end_time_time: string;
    employees_needed: number;
    work_area: string;
    notes: string;
    weekdays: number[];
    interval: number;
    repeat_until: string;
    never_ends: boolean;
    crm_task: number | null;
    crm_task_title: string;
  };
  isManager: boolean;
  workAreaIds: number[];
  onDefaultsChange: (defaults: { start_time_time: string; end_time_time: string; work_area: string }) => void;
}

function AddShiftModal({
  open,
  onClose,
  onSaved,
  workAreaOptions,
  initialData,
  isManager,
  workAreaIds,
  onDefaultsChange,
}: AddShiftModalProps) {
  const [shiftData, setShiftData] = useState(initialData);
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [taskOptions, setTaskOptions] = useState<CrmTask[]>([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const taskSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when initialData changes (modal re-opens)
  React.useEffect(() => {
    if (open) {
      setShiftData(initialData);
      setTaskSearchTerm('');
      setTaskOptions([]);
      setShowTaskDropdown(false);
    }
  }, [open, initialData]);

  const handleTaskSearch = useCallback(async (searchValue: string) => {
    if (taskSearchTimeoutRef.current) {
      clearTimeout(taskSearchTimeoutRef.current);
    }
    setTaskSearchTerm(searchValue);
    if (!searchValue || searchValue.length < 2) {
      setTaskOptions([]);
      setShowTaskDropdown(false);
      return;
    }
    taskSearchTimeoutRef.current = setTimeout(async () => {
      setTaskLoading(true);
      try {
        const tasks = await CrmTaskService.list({
          search: searchValue,
          status: 'OPEN,IN_PROGRESS',
          page_size: 15
        });
        setTaskOptions(tasks);
        setShowTaskDropdown(true);
      } catch (error) {
        console.error('Task search error:', error);
        setTaskOptions([]);
      } finally {
        setTaskLoading(false);
      }
    }, 300);
  }, []);

  const formatTaskLabel = (task: CrmTask): string => {
    const dueDate = task.due_date
      ? new Date(task.due_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
      : '';
    const partnerName = task.partner_name || '';
    return `${dueDate ? `[${dueDate}] ` : ''}${task.title}${partnerName ? ` – ${partnerName}` : ''}`;
  };

  const addOneHour = (time: string): string => {
    const [h, m] = time.split(':').map(Number);
    const newH = (h + 1) % 24;
    return `${String(newH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  if (!open) return null;

  const handleClose = () => {
    onClose();
  };

  const handleSaveNewShift = async () => {
    if (!shiftData.start_time_date) {
      toast.warning('Please select a start date');
      return;
    }
    if (!shiftData.work_area) {
      toast.warning('Bitte eine Work Area auswählen');
      return;
    }
    const startUtc = dateUtil.createTenantTimeAsUTC(shiftData.start_time_date, shiftData.start_time_time);
    let endUtc = dateUtil.createTenantTimeAsUTC(shiftData.start_time_date, shiftData.end_time_time);

    if (!startUtc || !endUtc) {
      toast.warning('Invalid date/time');
      return;
    }

    if (endUtc <= startUtc) {
      const nextDay = new Date(shiftData.start_time_date);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
      endUtc = dateUtil.createTenantTimeAsUTC(nextDayStr, shiftData.end_time_time) || endUtc;
    }

    let recurrence_rule = null;
    const hasRecurrence = shiftData.never_ends || shiftData.repeat_until;
    const allWeekdays = [0, 1, 2, 3, 4, 5, 6];
    const selectedWeekdays = shiftData.weekdays && shiftData.weekdays.length > 0
      ? shiftData.weekdays
      : hasRecurrence ? allWeekdays : [];

    if (selectedWeekdays.length > 0) {
      recurrence_rule = {
        weekdays: selectedWeekdays,
        interval: shiftData.interval || 1
      } as any;
      if (shiftData.repeat_until) {
        recurrence_rule.until = shiftData.repeat_until;
      }
    }

    const payload = {
      start_time: startUtc,
      end_time: endUtc,
      employees_needed: shiftData.employees_needed,
      work_area: shiftData.work_area || null,
      notes: shiftData.notes || '',
      recurrence_rule,
      never_ends: shiftData.never_ends,
      crm_task: shiftData.crm_task || null
    };

    try {
      await ShiftService.createShift(payload);
      onDefaultsChange({
        start_time_time: shiftData.start_time_time,
        end_time_time: shiftData.end_time_time,
        work_area: shiftData.work_area
      });
      handleClose();
      onSaved();
    } catch (error) {
      console.error('Error SHIFT creation =>', error);
      if (error instanceof HttpError) {
        if (!shiftErrorHandler.handle(error)) {
          globalErrorHandler.handle(error);
        }
      } else {
        toast.error('Shift creation failed: ' + (error as any).message);
      }
    }
  };

  const glassInputStyle: React.CSSProperties = {
    ...ps.inputStyle,
    padding: '4px 0px',
    fontSize: '12px',
    boxSizing: 'border-box',
  };

  const glassLabelStyle: React.CSSProperties = {
    ...ps.labelStyle,
    fontSize: '11px',
    marginBottom: 0,
  };

  const compactFieldWrapper: React.CSSProperties = {
    ...ps.fieldWrapper,
    padding: '4px 10px',
    borderRadius: 8,
  };

  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5001,
      }}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.bgCard,
          borderRadius: '14px',
          padding: '16px',
          width: '420px',
          maxHeight: '90vh',
          overflowY: 'auto',
          border: `1px solid ${colors.border}`,
          boxShadow: shadows.elevated,
        }}
        data-testid="add-shift-modal-div"
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: colors.navy }}>
            New Shift
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(0, 0, 0, 0.05)',
              border: 'none',
              borderRadius: '6px',
              width: '26px',
              height: '26px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: colors.textMuted,
              fontSize: '12px',
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {/* Start Date */}
          <div className="pa-field" style={compactFieldWrapper}>
            <label style={glassLabelStyle}>Date</label>
            <input
              type="date"
              value={shiftData.start_time_date}
              onChange={(e) => setShiftData({ ...shiftData, start_time_date: e.target.value })}
              style={glassInputStyle}
              data-testid="shift-start-date"
            />
          </div>

          {/* Start/End Time */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div className="pa-field" style={{ ...compactFieldWrapper, flex: 1 }}>
              <label style={glassLabelStyle}>Start Time</label>
              <input
                type="time"
                value={shiftData.start_time_time}
                onChange={(e) => {
                  const v = e.target.value;
                  const update: any = { ...shiftData, start_time_time: v };
                  if (v) update.end_time_time = addOneHour(v);
                  setShiftData(update);
                }}
                style={glassInputStyle}
                data-testid="shift-start-time"
              />
            </div>
            <div className="pa-field" style={{ ...compactFieldWrapper, flex: 1 }}>
              <label style={glassLabelStyle}>End Time</label>
              <input
                type="time"
                value={shiftData.end_time_time}
                onChange={(e) => setShiftData({ ...shiftData, end_time_time: e.target.value })}
                style={glassInputStyle}
                data-testid="shift-end-time"
              />
            </div>
          </div>

          {/* Employees Needed & Work Area */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div className="pa-field" style={{ ...compactFieldWrapper, flex: 1 }}>
              <label style={glassLabelStyle}>Employees Needed</label>
              <input
                type="number"
                min={1}
                value={shiftData.employees_needed}
                onChange={(e) => setShiftData({ ...shiftData, employees_needed: Number(e.target.value) })}
                style={glassInputStyle}
                data-testid="shift-employees-needed"
              />
            </div>
            <div className="pa-field" style={{ ...compactFieldWrapper, flex: 1 }}>
              <label style={glassLabelStyle}>Work Area</label>
              <select
                value={shiftData.work_area}
                onChange={(e) => setShiftData({ ...shiftData, work_area: e.target.value })}
                style={glassInputStyle}
                data-testid="shift-work-area"
              >
                {workAreaOptions
                  .filter(wa => !isManager || workAreaIds.includes(wa.id))
                  .map(wa => (
                    <option key={wa.id} value={wa.id}>{wa.name}</option>
                  ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="pa-field" style={compactFieldWrapper}>
            <label style={glassLabelStyle}>Notes</label>
            <textarea
              value={shiftData.notes}
              onChange={(e) => setShiftData({ ...shiftData, notes: e.target.value })}
              placeholder="Important notes for service chef, production and staff..."
              style={{
                ...glassInputStyle,
                minHeight: '50px',
                resize: 'vertical',
              }}
              data-testid="shift-notes"
            />
          </div>

          {/* Related Task */}
          <div className="pa-field" style={{ ...compactFieldWrapper, position: 'relative' }}>
            <label style={glassLabelStyle}>
              <FaTasks style={{ marginRight: 6, verticalAlign: 'middle', fontSize: '10px' }} />
              Related Task (optional)
            </label>
            {shiftData.crm_task ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 10px',
                background: `rgba(82, 113, 165, 0.1)`,
                borderRadius: 6,
                border: `1px solid rgba(82, 113, 165, 0.3)`
              }}>
                <FaTasks style={{ color: colors.slate, flexShrink: 0, fontSize: '10px' }} />
                <span style={{ flex: 1, fontSize: '12px', color: colors.textBody }}>
                  {shiftData.crm_task_title}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setShiftData({ ...shiftData, crm_task: null, crm_task_title: '' });
                    setTaskSearchTerm('');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: colors.textMuted,
                    fontSize: '10px'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={taskSearchTerm}
                  onChange={(e) => handleTaskSearch(e.target.value)}
                  onFocus={() => taskOptions.length > 0 && setShowTaskDropdown(true)}
                  placeholder="Search for task..."
                  style={glassInputStyle}
                />
                {taskLoading && (
                  <div style={{ position: 'absolute', right: 12, top: 28, color: colors.textMuted, fontSize: '10px' }}>
                    ...
                  </div>
                )}
                {showTaskDropdown && taskOptions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    boxShadow: shadows.medium,
                    maxHeight: 180,
                    overflowY: 'auto',
                    zIndex: 10
                  }}>
                    {taskOptions.map(task => (
                      <div
                        key={task.id}
                        onClick={() => {
                          setShiftData({
                            ...shiftData,
                            crm_task: task.id,
                            crm_task_title: formatTaskLabel(task)
                          });
                          setShowTaskDropdown(false);
                          setTaskSearchTerm('');
                        }}
                        style={{
                          padding: '8px 10px',
                          cursor: 'pointer',
                          borderBottom: `1px solid ${colors.borderLight}`,
                          fontSize: '11px',
                          color: colors.textBody,
                          transition: 'background 0.15s'
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = colors.bgHover)}
                        onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        {formatTaskLabel(task)}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Recurrence Section */}
          <div
            style={{
              background: 'rgba(14, 19, 51, 0.03)',
              borderRadius: '8px',
              padding: '10px',
              border: '1px solid rgba(14, 19, 51, 0.08)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: colors.slate }}>
                Recurrence
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '10px', color: colors.textBody }}>Never ends</span>
                <div
                  onClick={() => setShiftData({ ...shiftData, never_ends: !shiftData.never_ends })}
                  style={{
                    width: '32px',
                    height: '18px',
                    borderRadius: '9px',
                    background: shiftData.never_ends ? colors.slate : colors.border,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: shiftData.never_ends ? '16px' : '2px',
                      top: '2px',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      transition: 'left 0.2s',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Weekdays */}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {dayNames.map((day, idx) => {
                const isChecked = shiftData.weekdays.includes(idx);
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      if (isChecked) {
                        setShiftData(prev => ({ ...prev, weekdays: prev.weekdays.filter(x => x !== idx) }));
                      } else {
                        setShiftData(prev => ({ ...prev, weekdays: [...prev.weekdays, idx] }));
                      }
                    }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: isChecked ? 600 : 500,
                      background: isChecked ? `rgba(82, 113, 165, 0.15)` : colors.bgCard,
                      color: isChecked ? colors.slate : colors.textMuted,
                      border: isChecked ? `1px solid ${colors.slate}` : `1px solid ${colors.border}`,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {day}
                  </div>
                );
              })}
            </div>

            {/* Interval & Repeat until */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <div className="pa-field" style={{ ...compactFieldWrapper, flex: 1 }}>
                <label style={{ ...glassLabelStyle, fontSize: '10px' }}>Interval (weeks)</label>
                <input
                  type="number"
                  min={1}
                  value={shiftData.interval}
                  onChange={(e) => setShiftData({ ...shiftData, interval: Number(e.target.value) })}
                  style={glassInputStyle}
                />
              </div>
              <div className="pa-field" style={{ ...compactFieldWrapper, flex: 1 }}>
                <label style={{ ...glassLabelStyle, fontSize: '10px' }}>Repeat until</label>
                <input
                  type="date"
                  value={shiftData.repeat_until}
                  onChange={(e) => setShiftData({ ...shiftData, repeat_until: e.target.value })}
                  disabled={shiftData.never_ends}
                  style={{ ...glassInputStyle, opacity: shiftData.never_ends ? 0.5 : 1 }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button
            onClick={handleClose}
            style={{
              ...ps.buttonSecondary,
              padding: '8px 16px',
              fontSize: '12px',
            }}
            data-testid="cancel-add-shift"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveNewShift}
            style={{
              ...ps.buttonPrimary,
              padding: '8px 16px',
              fontSize: '12px',
            }}
            data-testid="save-shift"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddShiftModal;
